import React from 'react';
import useBreakpoints from '../hooks/useBreakPoint';
import Track from './Track';
import ArrowLeft from './icons/ArrowLeft';
import ArrowRight from './icons/ArrowRight';

const Carousel = ({
  children: slides,
  slidesToShow = 1,
  slidesToScroll = 1,
  gap = 0,
  slidingSpeed = 500,
  infiniteAutoplay = false,
  slideDisplayTime = 5700,
  circular = false,
  innerBottomRight = false,
  height = 'h-full',
  buttonShadow = false,
  className,
}) => {
  const { is2xl } = useBreakpoints();
  const trackContainerRef = React.useRef(null);
  const trackRef = React.useRef(null);

  const [scrollIndex, setScrollIndex] = React.useState(() =>
    circular ? slidesToShow : 0,
  );
  const [initialTouchPosition, setInitialTouchPosition] = React.useState(null);
  const [isScrolling, setIsScrolling] = React.useState(false);
  // unit: px
  const [slideWidth, setSlideWidth] = React.useState(null);
  const [slideLength] = React.useState(slides.length);

  React.useEffect(() => {
    setSlideWidth(
      Math.round(trackContainerRef.current.offsetWidth / slidesToShow),
    );
  }, [slidesToShow]);

  React.useEffect(() => {
    const accumulatedShiftValue = scrollIndex * slideWidth;
    // move the track
    trackRef.current.style.transform = `translateX(-${accumulatedShiftValue}px)`;
  }, [scrollIndex, slideWidth]);

  React.useEffect(() => {
    if (!circular) return;
    trackRef.current.addEventListener('transitionend', async () => {
      if (trackRef.current.style.transform === 'translateX(0px)') {
        // stop the animation
        trackRef.current.style.transitionProperty = 'none';
        // jump to the visually same position
        setTimeout(() => {
          trackRef.current.style.transform = `translateX(-${
            slideLength * slideWidth
          }px)`;
        }, 10);
        // resume the animation
        setTimeout(() => {
          trackRef.current.style.transitionProperty = 'transform';
          setScrollIndex(slideLength);
        }, 20);
      } else if (
        trackRef.current.style.transform ===
        `translateX(-${(slideLength + slidesToShow) * slideWidth}px)`
      ) {
        // stop the animation
        trackRef.current.style.transitionProperty = 'none';
        // jump to the visually same position
        setTimeout(() => {
          trackRef.current.style.transform = `translateX(-${
            slidesToShow * slideWidth
          }px)`;
        }, 10);
        // resume the animation
        setTimeout(() => {
          trackRef.current.style.transitionProperty = 'transform';
          setScrollIndex(slidesToShow);
        }, 20);
      }
    });
  }, [scrollIndex, slideWidth, slideLength, slidesToShow, circular]);

  const handleNext = () => {
    setScrollIndex(i => i + slidesToScroll);
  };

  const handlePrev = () => {
    setScrollIndex(i => i - slidesToScroll);
  };

  const handlePointerDown = event => {
    setIsScrolling(true);
    setInitialTouchPosition(event.pageX);
  };

  const handlePointerMove = event => {
    if (isScrolling) {
      const accumulatedShiftValue = scrollIndex * slideWidth;
      const currentTouchPosition = event.pageX;
      const shiftFromInitial = currentTouchPosition - initialTouchPosition;
      // move the track
      trackRef.current.style.transform = `translateX(${
        -accumulatedShiftValue + shiftFromInitial
      }px)`;
    }
  };

  const handlePointerUp = event => {
    setIsScrolling(false);
    const currentTouchPosition = event.pageX;
    const newShift = currentTouchPosition - initialTouchPosition;
    const accumulatedShiftValue = scrollIndex * slideWidth;

    const minShiftValue = 0;
    const maxShiftValue =
      trackRef.current.offsetWidth - trackContainerRef.current.offsetWidth;

    // IF reach the end or the beginning, move trackRef back
    if (-accumulatedShiftValue + newShift >= minShiftValue) {
      trackRef.current.style.transform = `translateX(-${minShiftValue}px)`;
    } else if (-accumulatedShiftValue + newShift <= -maxShiftValue) {
      trackRef.current.style.transform = `translateX(-${maxShiftValue}px)`;
    } else if (newShift < 0) {
      handleNext();
    } else if (newShift > 0) {
      handlePrev();
    }
  };

  return (
    // carousel container
    <div className={`relative ${className}`}>
      {/* track container */}
      <div
        ref={trackContainerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className={`overflow-hidden w-full ${height}`}
        style={{ touchAction: 'none' }}
      >
        {/* track */}
        <Track
          ref={trackRef}
          totalSlideNumber={slideLength}
          slideWidth={slideWidth}
          slideDisplayTime={slideDisplayTime}
          infiniteAutoplay={infiniteAutoplay}
          className={`inline-flex align-top h-full transition-transform transform duration-${slidingSpeed}`}
        >
          {!infiniteAutoplay &&
            circular &&
            slides
              .filter((slide, index) => index >= slideLength - slidesToShow)
              .map((
                slide, // slide container
              ) => (
                <div
                  key={`${slide.key}-circular-front`}
                  className={`flex-shrink-0 pr-${parseFloat(
                    Math.round(gap / 4),
                  )}`}
                  style={{ width: slideWidth }}
                >
                  {slide}
                </div>
              ))}

          {slides.map(slide => (
            // slide container
            <div
              key={slide.key}
              className={`flex-shrink-0 pr-${parseFloat(Math.round(gap / 4))}`}
              style={{ width: slideWidth }}
            >
              {slide}
            </div>
          ))}

          {!infiniteAutoplay &&
            circular &&
            slides
              .filter((slide, index) => index < slidesToShow)
              .map(slide => (
                // slide container
                <div
                  key={`${slide.key}-circular-back`}
                  className={`flex-shrink-0 pr-${parseFloat(
                    Math.round(gap / 4),
                  )}`}
                  style={{ width: slideWidth }}
                >
                  {slide}
                </div>
              ))}

          {/* simply double the slides, but can be reduced based on slidesToShow */}
          {infiniteAutoplay &&
            slides.map(slide => (
              // slide container
              <div
                key={`${slide.key}-infiniteAutoplay`}
                className={`flex-shrink-0 pr-${parseFloat(
                  Math.round(gap / 4),
                )}`}
                style={{ width: slideWidth }}
              >
                {slide}
              </div>
            ))}
        </Track>
      </div>

      {!infiniteAutoplay && (
        <div
          className={`absolute ${
            innerBottomRight
              ? 'bottom-2 right-2 lg:bottom-4 lg:right-3 3xl:bottom-5'
              : '-top-14 left-5 md:left-22 lg:-top-16 lg:left-32 xl:-top-14 2xl:-top-28 2xl:left-36 3xl:left-46'
          }`}
        >
          {/* prev arrow */}
          <button
            type="button"
            className="text-white p-2 disabled:opacity-50 disabled:cursor-default"
            onClick={handlePrev}
            disabled={scrollIndex === 0}
          >
            <ArrowLeft
              isShadowOn={buttonShadow}
              width={is2xl ? '24' : '12'}
              height={is2xl ? '40' : '20'}
            />
          </button>
          {/* next arrow */}
          <button
            type="button"
            className={`text-white p-2 disabled:opacity-50 disabled:cursor-default ${
              innerBottomRight ? '2xl:ml-6' : 'ml-4 md:ml-4 2xl:ml-8 3xl:ml-9'
            }`}
            onClick={handleNext}
            disabled={
              circular
                ? scrollIndex === slideLength + slidesToShow
                : scrollIndex === slides.length - slidesToShow
            }
          >
            <ArrowRight
              isShadowOn={buttonShadow}
              width={is2xl ? '24' : '12'}
              height={is2xl ? '40' : '20'}
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default Carousel;
